import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import MetricsGraph from '@avtechsoftware/react-component-library/src/components/metrics/MetricsGraph/MetricsGraph';
import moment from 'moment-timezone';
import emptyFunction from '@avtechsoftware/ra-javascript-utilities/src/utilities/EmptyFunction';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import SensorListUtils from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/SensorListUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ThresholdRangeService from '@avtechsoftware/ra-javascript-utilities/src/utilities/metricsGroups/ThresholdRangeService';
import GraphOverlayService from '@avtechsoftware/ra-javascript-utilities/src/utilities/metricsGroups/GraphOverlayService';
import { ReportAxis } from '@avtechsoftware/ra-javascript-utilities/src/types/ReportAxis';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import { withRouter } from 'react-router-dom';
import envConfig from '../../../envConfig';
import timeZoneList from '../../../utility/TimeZoneList';
import ReportChannelLegend from '../../metrics/ReportChannelLegend/ReportChannelLegend';

class PublicReportGraph extends React.Component {
  constructor(props) {
    super(props);
    const { timeZone, tempScale } = props;
    this.state = {
      sensorMap: {},
      reportDetails: { name: 'Unknown Report' },
      deviceList: [],
      channelKeyList: [],
      channelIdList: [],
      thresholdRangeAssociations: [],
      timeZone: timeZoneList.find((tzObject) => tzObject.value === timeZone) ? timeZone : 'UTC',
      temperatureScale: (tempScale.toLowerCase() === 'c' || tempScale.toLowerCase() === 'f') ? tempScale.toUpperCase() : 'F',
      reportFound: true,
      dataLoaded: false,
    };

    this.getReportMetaData = this.getReportMetaData.bind(this);
    this.getMasterDeviceListObject = this.getMasterDeviceListObject.bind(this);
    this.setTemperatureScale = this.setTemperatureScale.bind(this);
    this.setTimeZone = this.setTimeZone.bind(this);
  }

  componentDidMount() {
    const {
      history, publicReportHashKey,
    } = this.props;
    const { timeZone, temperatureScale } = this.state;
    history.replace(`/public/report/${publicReportHashKey}/?tempScale=${temperatureScale}&timezone=${timeZone}`);
    this.getReportMetaData().then(() => {
      this.intervalId = setInterval(this.getReportMetaData, 30000);
    });
  }

  componentWillUnmount() {
    clearInterval(this.intervalId);
  }

  getReportMetaData() {
    const { publicReportHashKey } = this.props;
    return axios.get(`${envConfig.apiUrl}/api/v1/device-data/public/report/${publicReportHashKey}`)
      .then((response) => {
        this.setState({
          sensorMap: response.data.data.sensorMap,
          reportDetails: response.data.data.reportDetails,
          deviceList: response.data.data.deviceList
            .map((device) => ({ ...device, name: device.deviceName })),
          channelKeyList: response.data.data.channelKeyList,
          channelIdList: response.data.data.channelIdList,
          thresholdRangeAssociations: response.data.data.thresholdRangeAssociations,
          reportFound: true,
          dataLoaded: true,
        });
      })
      .catch(() => {
        this.setState({
          sensorMap: {},
          reportDetails: { name: 'Unknown Report' },
          deviceList: [],
          channelKeyList: [],
          channelIdList: [],
          thresholdRangeAssociations: [],
          reportFound: false,
          dataLoaded: true,
        });
      });
  }

  getThresholdRangeContext() {
    const { thresholdRangeAssociations } = this.state;
    const thresholdRanges = thresholdRangeAssociations.map(
      (thresholdRangeAssociation) => thresholdRangeAssociation.thresholdRange,
    );

    return {
      findById: (id) => thresholdRanges.find((thresholdRange) => thresholdRange.id === id),
      findAll: () => thresholdRanges,
    };
  }

  getMasterDeviceListObject() {
    const { sensorMap, deviceList } = this.state;
    return {
      getSensorMap: () => sensorMap,
      getFullDeviceList: () => deviceList,
      findByUUID: (uuidOrMacAddress) => deviceList.find(
        (device) => device.macAddress === uuidOrMacAddress,
      ),
    };
  }

  setTemperatureScale(event) {
    const { history, publicReportHashKey } = this.props;
    const { timeZone } = this.state;
    const { target } = event;
    history.replace(`/public/report/${publicReportHashKey}/?tempScale=${target.value}&timezone=${timeZone}`);
    this.setState({ temperatureScale: target.value });
  }

  setTimeZone(event) {
    const { history, publicReportHashKey } = this.props;
    const { temperatureScale } = this.state;
    const { target } = event;
    history.replace(`/public/report/${publicReportHashKey}/?tempScale=${temperatureScale}&timezone=${target.value}`);
    this.setState({ timeZone: target.value });
  }

  getChannelList() {
    const { channelKeyList, sensorMap } = this.state;
    return channelKeyList.map((channelKey) => {
      const detailedSensorInfo = SensorListUtils.getDetailedSensorInfoFromChannelKey(
        channelKey, this.getMasterDeviceListObject().findByUUID(channelKey.macAddress), sensorMap,
      );
      return {
        ...channelKey,
        color: channelKey.color,
        sensorName: detailedSensorInfo.sensorLabel,
        channelName: detailedSensorInfo.channelName,
        axis: channelKey.axis,
      };
    });
  }

  render() {
    const {
      reportDetails,
      timeZone,
      temperatureScale,
      thresholdRangeAssociations,
      reportFound,
      dataLoaded,
      channelIdList,
    } = this.state;
    if (!reportFound) {
      return (
        <div className="alert alert-warning">
          <Icon icon="triangle-exclamation" />
          {' '}
          Invalid Report Link
        </div>
      );
    }
    const { publicReportHashKey } = this.props;
    const masterDeviceList = this.getMasterDeviceListObject();
    const endTime = reportDetails.timeInterval > 0
      ? new Date(Date.now())
      : new Date(reportDetails.endDate);
    const startTime = reportDetails.timeInterval > 0
      ? moment(new Date()).subtract(reportDetails.timeInterval, 'seconds').toDate()
      : new Date(reportDetails.startDate);
    const currentTimestamp = moment()
      .format('yyyy-MM-DD_hh-mm-ss');
    // eslint-disable-next-line no-control-regex
    const csvGroupName = reportDetails.name.replace(/[^\x00-\x7F]/g, '_')
      .replace(/  */g, '_');
    const channelList = this.getChannelList();

    const csvStartTime = Math.round(moment(startTime)
      .valueOf() / 1000);
    const csvEndTime = Math.round(moment(endTime)
      .valueOf() / 1000);

    let overlayShapes = [];
    const graphOverlays = ThresholdRangeService.getGraphOverlaysFromThresholdRangeAssociations(
      thresholdRangeAssociations, this.getThresholdRangeContext(),
    );
    const yMaxValue = GraphOverlayService.getMaxYValue(graphOverlays,
      ReportAxis.LEFT,
      temperatureScale);
    const y2MaxValue = GraphOverlayService.getMaxYValue(graphOverlays,
      ReportAxis.RIGHT,
      temperatureScale);
    GraphOverlayService.convertPredefinedGreaterThanToBetween(
      graphOverlays,
    ).forEach((item) => {
      overlayShapes = overlayShapes.concat(item.getOverlayShapes(temperatureScale));
    });
    return (
      <>
        {dataLoaded ? (
          <div>
            <div className="row my-3">
              <div className="col-12 mx-auto">
                <div className="row form-inline">
                  <div className="col-auto mr-auto form-inline">
                    <Icon icon="chart-area" className="mr-1" size="lg" />
                    <span style={{ fontSize: '22px', letterSpacing: '-1px' }}>{reportDetails.name}</span>
                  </div>
                  <div className="col-auto ml-auto form-check">
                    <input
                      type="radio"
                      className="mx-1"
                      value="F"
                      checked={temperatureScale.toLowerCase() === 'f'}
                      onChange={this.setTemperatureScale}
                    />
                    {' '}
                    Fahrenheit
                  </div>
                  <div className="col-auto form-check">
                    <input
                      type="radio"
                      className="mx-1"
                      value="C"
                      checked={temperatureScale.toLowerCase() === 'c'}
                      onChange={this.setTemperatureScale}
                    />
                    {' '}
                    Celsius
                  </div>
                  <div className="col-auto">
                    <select className="form-control form-control-sm" onChange={this.setTimeZone} value={timeZone}>
                      {timeZoneList.map(
                        (timeZoneObject, index) => (
                          <option
                            value={timeZoneObject.value}
                            key={index}
                          >
                            {timeZoneObject.name}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div className="col-auto">
                    <a
                      href={`${envConfig.apiUrl}/api/v1/device-data/public/samples/csv?publicHash=${publicReportHashKey}&startTime=${csvStartTime}&endTime=${csvEndTime}&temperatureScale=${temperatureScale}&timeZone=${timeZone}&reportName=${reportDetails.name}_${currentTimestamp}.csv`}
                      rel="noopener noreferrer"
                      download={`${csvGroupName}_${currentTimestamp}.csv`}
                      className="text-reset text-decoration-none"
                    >
                      <FontAwesomeIcon icon="file-csv" size="lg" />
                    </a>
                  </div>
                  <div className="col-auto">
                    <a
                      rel="noopener noreferrer"
                      href={`/public/report-pdf/?reportPublicLinkHash=${publicReportHashKey}&timezone=${timeZone}&tempScale=${temperatureScale}&channelReportName=${reportDetails.name}&channelIds=${channelIdList}&startTime=${startTime.toISOString()}&endTime=${endTime.toISOString()}`}
                      className="text-reset text-decoration-none"
                    >
                      <Icon icon="file-pdf" size="lg" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mx-auto">
                <MetricsGraph
                  channelList={channelList}
                  startTime={startTime}
                  endTime={endTime}
                  jwt=""
                  masterDeviceList={masterDeviceList}
                  onUpgradeClick={emptyFunction}
                  reportDataUrlBase={`${envConfig.apiUrl}/api/v1/device-data/public/report/data/${publicReportHashKey}`}
                  size="300px"
                  temperatureScale={temperatureScale}
                  timeZoneName={timeZone}
                  overlayShapes={overlayShapes}
                  maxYAxisValue={yMaxValue}
                  maxY2AxisValue={y2MaxValue}
                  displayEmptyGraph={(yMaxValue !== undefined || y2MaxValue !== undefined)}
                  hasAdminPrivileges={false}
                />
              </div>
            </div>
            <div className="card mt-3">
              <div className="card-body">
                <ReportChannelLegend channelList={channelList} />
              </div>
            </div>
          </div>
        )
          : ((<div className="d-flex align-items-center justify-content-center"><ProgressSpinner /></div>))}
      </>
    );
  }
}

PublicReportGraph.defaultProps = {
  tempScale: 'F',
  timeZone: 'UTC',
};

PublicReportGraph.propTypes = {
  publicReportHashKey: PropTypes.string.isRequired,
  tempScale: PropTypes.string,
  timeZone: PropTypes.string,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};

export { PublicReportGraph };
export default withRouter(PublicReportGraph);
